import { compact } from "lodash";
import moment from "moment";
import { useMemo } from "react";
import { ComplianceProgramAppliesTo } from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import RecipientForm from "@apps/artifact/components/recipient-form";
import TeamPicker from "@apps/reporting/components/team-picker";
import useLabel from "@apps/use-label/use-label";
import { currentUserVar, isAdminVar } from "@cache/cache";
import ComboboxGeneric from "@components/combobox/generic-combobox";
import FormFields from "@components/form/form-fields";
import InputDatePicker from "@components/input/input-datepicker";
import { assertNonNull } from "@helpers/helpers";

import { ProgramForm } from "../types";
import ProgramFormParticipantsPreview from "./program-form-participants-preview";

const ProgramFormParticipants = ({
  creator,
  canUpdate,
  proposedComplianceProgram,
  onUpdateProposedComplianceProgram,
}: {
  creator: BasicUser;
  canUpdate: boolean;
  proposedComplianceProgram: ProgramForm;
  onUpdateProposedComplianceProgram: (data: Partial<ProgramForm>) => void;
}) => {
  const label = useLabel();
  const isAdmin = isAdminVar();
  const currentUser = currentUserVar();

  const appliesToOptions = useMemo(
    () =>
      compact([
        isAdmin && {
          value: ComplianceProgramAppliesTo.Organization,
          label: "All employees",
        },
        isAdmin && {
          value: ComplianceProgramAppliesTo.Managers,
          label: "All managers",
        },
        isAdmin && {
          value: ComplianceProgramAppliesTo.Ics,
          label: "All individual contributors (ICs)",
        },
        {
          value: ComplianceProgramAppliesTo.CreatorDirectReports,
          label: `${
            creator.id === currentUser.id
              ? "All my"
              : `All of ${creator.name}'s`
          } direct reports`,
        },
        {
          value: ComplianceProgramAppliesTo.CreatorManagementTree,
          label: `${
            creator.id === currentUser.id
              ? "All my"
              : `All of ${creator.name}'s`
          } reports (full reporting tree)`,
        },
        {
          value: ComplianceProgramAppliesTo.Departments,
          label: `All employees in specific ${label("team", {
            pluralize: true,
          })}`,
        },
        {
          value: ComplianceProgramAppliesTo.ReportsTo,
          label: "All employees that report to specific managers",
        },
        {
          value: ComplianceProgramAppliesTo.Users,
          label: "Specific employees",
        },
      ]),
    [creator, currentUser, isAdmin, label],
  );

  return (
    <>
      <div className="border rounded-md p-6">
        <div className="tf-header-base-dark pb-3">Included Participants</div>
        <FormFields
          fields={compact([
            {
              title: "Applies to",
              render: () => (
                <ComboboxGeneric
                  options={appliesToOptions}
                  disabled={!canUpdate}
                  onChangeValue={(opt) =>
                    onUpdateProposedComplianceProgram({
                      ...proposedComplianceProgram,
                      appliesTo: opt.value,
                    })
                  }
                  value={assertNonNull(
                    appliesToOptions.find(
                      ({ value }) =>
                        value === proposedComplianceProgram.appliesTo,
                    ),
                  )}
                />
              ),
            },
            proposedComplianceProgram.appliesTo ===
              ComplianceProgramAppliesTo.Departments && {
              title: `${label("team", { pluralize: true, capitalize: true })}`,
              render: () => (
                <TeamPicker
                  canChange={canUpdate}
                  teams={proposedComplianceProgram.appliesToTeams}
                  onAddTeam={(team) => {
                    onUpdateProposedComplianceProgram({
                      appliesToTeams:
                        proposedComplianceProgram.appliesToTeams.concat(team),
                    });
                  }}
                  onRemoveTeam={(team) => {
                    onUpdateProposedComplianceProgram({
                      appliesToTeams:
                        proposedComplianceProgram.appliesToTeams.filter(
                          (t) => t.id !== team.id,
                        ),
                    });
                  }}
                />
              ),
            },
            proposedComplianceProgram.appliesTo ===
              ComplianceProgramAppliesTo.ReportsTo && {
              title: "Managers",
              render: () => (
                <RecipientForm
                  canChange={canUpdate}
                  showCurrentUser
                  recipients={proposedComplianceProgram.appliesToManagers}
                  onAddRecipient={(user) =>
                    onUpdateProposedComplianceProgram({
                      appliesToManagers:
                        proposedComplianceProgram.appliesToManagers.concat(
                          user,
                        ),
                    })
                  }
                  onRemoveRecipient={(user) =>
                    onUpdateProposedComplianceProgram({
                      appliesToManagers:
                        proposedComplianceProgram.appliesToManagers.filter(
                          (u) => u.id !== user.id,
                        ),
                    })
                  }
                />
              ),
            },
            proposedComplianceProgram.appliesTo ===
              ComplianceProgramAppliesTo.Users && {
              title: "Employees",
              render: () => (
                <RecipientForm
                  canChange={canUpdate}
                  showCurrentUser
                  recipients={proposedComplianceProgram.appliesToUsers}
                  onAddRecipient={(user) =>
                    onUpdateProposedComplianceProgram({
                      appliesToUsers:
                        proposedComplianceProgram.appliesToUsers.concat(user),
                    })
                  }
                  onRemoveRecipient={(user) =>
                    onUpdateProposedComplianceProgram({
                      appliesToUsers:
                        proposedComplianceProgram.appliesToUsers.filter(
                          (u) => u.id !== user.id,
                        ),
                    })
                  }
                />
              ),
            },
          ])}
        />
      </div>

      <div className="mt-6 border rounded-md p-6">
        <div className="tf-header-base-dark pb-3">Excluded Participants</div>
        <FormFields
          fields={compact([
            {
              title: "Excluded employees",
              render: () => (
                <RecipientForm
                  canChange={canUpdate}
                  showCurrentUser
                  recipients={proposedComplianceProgram.excludedUsers}
                  onAddRecipient={(user) =>
                    onUpdateProposedComplianceProgram({
                      excludedUsers:
                        proposedComplianceProgram.excludedUsers.concat(user),
                    })
                  }
                  onRemoveRecipient={(user) =>
                    onUpdateProposedComplianceProgram({
                      excludedUsers:
                        proposedComplianceProgram.excludedUsers.filter(
                          (u) => u.id !== user.id,
                        ),
                    })
                  }
                />
              ),
            },
            {
              title: "Exclude hired after",
              render: () => (
                <div className="flex items-center gap-4">
                  <InputDatePicker
                    date={proposedComplianceProgram.excludedHireDateAfter}
                    onChangeDate={(date) =>
                      onUpdateProposedComplianceProgram({
                        ...proposedComplianceProgram,
                        excludedHireDateAfter: date
                          ? moment(date).format("YYYY-MM-DD")
                          : undefined,
                      })
                    }
                    wrapperClassName="w-full"
                    placeholderText=""
                    isClearable
                    disabled={!canUpdate}
                    ariaLabelledBy="Exclude hired after date"
                  />
                </div>
              ),
            },
          ])}
        />
      </div>

      <ProgramFormParticipantsPreview
        proposedComplianceProgram={proposedComplianceProgram}
      />
    </>
  );
};

export default ProgramFormParticipants;
